/**
 * The document fetched beside the session check (1.X deliverable 12): one
 * module-level slot. `docBeforeLoad` calls `prefetchDoc(id)` before it awaits
 * `auth.me`, so `docs.get` no longer waits for that round trip; the page's
 * session takes the answer through `syncServices`' first `getDoc`.
 *
 * - `takePrefetched(id)` hands the prefetch over once, only for its id and only
 *   while it is at most `PREFETCH_MAX_AGE_MS` old; otherwise null, and the
 *   caller fetches. Opening still sends one `docs.get`.
 * - A rejected prefetch is caught where it is stored, so it never raises an
 *   unhandled rejection; it rejects only for the one who takes it (a 401 still
 *   goes to `/login?next=…` through the session check).
 * - `resetPrefetch()` empties the slot (specs).
 */
import { type DocumentV1, docsGet } from '@codex/contracts';
import { api } from '../auth/client.js';
import { markOpen } from './open-trace.js';

/** How old a prefetch may be when the page takes it. */
export const PREFETCH_MAX_AGE_MS = 3_000;

export type FetchDoc = (docId: string) => Promise<DocumentV1>;
export type Now = () => number;

const fetchDoc: FetchDoc = (docId) => api(docsGet, { params: { id: docId } });
const wallClock: Now = () => Date.now();

interface Slot {
  readonly docId: string;
  readonly startedAt: number;
  readonly answer: Promise<DocumentV1>;
}

let slot: Slot | null = null;

/** Starts `docs.get` for `docId` now and keeps its answer for the page (replacing any other); returns that answer. */
export function prefetchDoc(
  docId: string,
  fetch: FetchDoc = fetchDoc,
  now: Now = wallClock,
): Promise<DocumentV1> {
  let answer: Promise<DocumentV1>;
  try {
    answer = fetch(docId);
  } catch (error) {
    answer = Promise.reject(error);
  }
  answer.then(
    () => markOpen('docs-get'),
    () => undefined,
  );
  slot = { docId, startedAt: now(), answer };
  return answer;
}

/** The prefetched answer for `docId`, once and while fresh; null otherwise (the caller fetches). */
export function takePrefetched(docId: string, now: Now = wallClock): Promise<DocumentV1> | null {
  const held = slot;
  if (held === null || held.docId !== docId) return null;
  slot = null;
  if (now() - held.startedAt > PREFETCH_MAX_AGE_MS) return null;
  return held.answer;
}

/** Empties the slot (specs). */
export function resetPrefetch(): void {
  slot = null;
}
